import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Typography,
  Button,
  Modal,
  Input,
  Card,
  Row,
  Col,
  message,
  Skeleton,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { Customer } from '../../types/customers';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getCustomers, deleteCustomer } from '../../api/customers';
import { API_STALE_TIME_MS } from '../../constants/api';
import type { QueryDocumentSnapshot } from 'firebase/firestore';
import CustomerModal from '../../components/sales/CustomerModal';
import CustomersTable from '../../components/sales/CustomersTable';

const { Title, Paragraph } = Typography;

const PAGE_SIZE = 10;

const Customers: React.FC = () => {
  const { t } = useTranslation(['sales', 'common']);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // State
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [lastDocs, setLastDocs] = useState<{ [page: number]: QueryDocumentSnapshot | undefined }>({});
  const [hasNext, setHasNext] = useState(false);
  const [isAddCustomerOpen, setIsAddCustomerOpen] = useState(false);

  const { data: customersData = { customers: [], lastDoc: undefined }, isLoading } = useQuery<{ customers: Customer[]; lastDoc?: QueryDocumentSnapshot }>({
    queryKey: ['customers', page],
    queryFn: async () => {
      const lastDoc = page > 1 ? lastDocs[page - 1] : undefined;
      return getCustomers(PAGE_SIZE, lastDoc);
    },
    staleTime: API_STALE_TIME_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    placeholderData: (previousData) => previousData,
  });

  useEffect(() => {
    if (customersData.lastDoc) {
      setLastDocs(prev => ({ ...prev, [page]: customersData.lastDoc }));
      setHasNext(!!customersData.lastDoc && customersData.customers.length === PAGE_SIZE);
    }
  }, [customersData, page]);

  const customers = customersData.customers;

  const deleteCustomerMutation = useMutation({
    mutationFn: (id: string) => deleteCustomer(id),
    onSuccess: () => {
      message.success(t('sales:success.deleteSuccess', { entity: t('customer') }));
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setOpenDeleteDialog(false);
      setDeleteId(null);
    },
    onError: () => {
      message.error(t('sales:errors.deleteFailed', { entity: t('customer') }));
    },
  });

  // Memoized handlers
  const handleTableChange = useCallback((pagination: any) => {
    if (pagination.current > page && hasNext) {
      setPage(pagination.current);
    } else if (pagination.current < page && page > 1) {
      setPage(pagination.current);
    }
  }, [page, hasNext]);

  const handleAdd = useCallback(() => {
    setEditingCustomer(null);
    setIsAddCustomerOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (deleteId) {
      deleteCustomerMutation.mutate(deleteId);
    }
  }, [deleteId, deleteCustomerMutation]);

  const handleCancelDelete = useCallback(() => {
    setOpenDeleteDialog(false);
    setDeleteId(null);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setIsAddCustomerOpen(false);
    setEditingCustomer(null);
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleModalSuccess = useCallback(() => {
    setIsAddCustomerOpen(false);
    setEditingCustomer(null);
    queryClient.invalidateQueries({ queryKey: ['customers'] });
  }, [queryClient]);

  const handleEdit = useCallback((customer: Customer) => {
    setEditingCustomer(customer);
    setIsAddCustomerOpen(true);
  }, []);

  const handleView = useCallback((customer: Customer) => {
    navigate(`/sales/customers/${customer.id}`, { state: { customer } });
  }, [navigate]);

  const filteredCustomers = useMemo(() => 
    customers.filter((customer: Customer) => {
      const propertyAddress = customer.addresses?.find(a => a.type === 'Property');
      return (
        customer.primary_contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.primary_contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.primary_contact.phone.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (propertyAddress?.city?.toLowerCase() || '').includes(searchTerm.toLowerCase())
      );
    }),
    [customers, searchTerm]
  );

  const pagination = useMemo(() => ({
    current: page,
    pageSize: PAGE_SIZE,
    total: hasNext ? (page + 1) * PAGE_SIZE : page * PAGE_SIZE,
    onChange: (newPage: number) => setPage(newPage),
  }), [page, hasNext]);

  return (
    <div style={{ width: '100%' }}>
      {isLoading && filteredCustomers.length === 0 ? (
        <Card style={{ marginTop: 24 }}><Skeleton active /></Card>
      ) : (
        <>
          <Row 
            justify="space-between" 
            align="middle" 
            style={{ marginBottom: 24, marginTop: 24}}
            gutter={[{ xs: 8, sm: 16 }, { xs: 8, sm: 16 }]} 
          >
            <Col xs={24} sm={16} md={18}>
              <Input
                placeholder={t('sales:customers.searchPlaceholder')}
                value={searchTerm}
                onChange={handleSearchChange}
                prefix={<SearchOutlined />}
                allowClear
              />
            </Col>
            <Col xs={24} sm={8} md={6} style={{ textAlign: 'right' }}>
              <Button
                type="primary"
                onClick={handleAdd}
                icon={<PlusOutlined />}
              >
                {t('sales:customers.add')}
              </Button>
            </Col>
          </Row>

          <Card variant="borderless" style={{ boxShadow: '0 0 10px rgba(0,0,0,0.1)' }}>
            <CustomersTable
              customers={filteredCustomers}
              loading={isLoading}
              pagination={pagination}
              onEdit={handleEdit}
              onView={handleView}
              onTableChange={handleTableChange}
            />
          </Card>

          <CustomerModal
            open={isAddCustomerOpen}
            customer={editingCustomer}
            onSuccess={handleModalSuccess}
            onCancel={handleCloseDialog}
          />

          <Modal
            title={<Title level={5}>{t('common:confirmDeleteTitle')}</Title>}
            open={openDeleteDialog}
            onOk={handleConfirmDelete}
            onCancel={handleCancelDelete}
            okText={t('common:delete')}
            cancelText={t('common:cancel')}
            okButtonProps={{ danger: true }}
            width="400px"
            destroyOnHidden
          >
            <Paragraph>{t('common:confirmDelete')}</Paragraph>
          </Modal>
        </>
      )}
    </div>
  );
};

export default React.memo(Customers); 